import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EcomEngineAPI from 'api/ecom_engine_api';
import { generateURL } from 'utils/helpers';
import { isBookingPaid, useVeroTracking } from 'utils/tracking';
import EcomComponentWrapper from 'components/ecom_component_wrapper';
import WalletPasses from 'components/wallet_passes';
import { fetchDocuments } from 'redux/actions/booking';
import { submitToGoogleAnalytics } from 'redux/actions/google_analytics';
import { getCurrentBooking } from 'redux/selectors/current_booking';
import { resetCards } from 'redux/slice/gift_card';
import CartSummary from '../cart_summary';
import NewsletterSubscribe from '../newsletter_subscribe';
import RelatedProducts from '../related_products';

export function ConfirmBooking() {
  const booking = useSelector(getCurrentBooking);
  const {
    reference,
    id: bookingId,
    customer_contact_name: contactName,
    customer_contact_email: contactEmail,
  } = booking;
  const documents = useSelector((state) => state.booking.documents);
  const surchargePaymentTypeId = useSelector(
    (state) => state.checkout.surchargePaymentTypeId
  );
  const dispatch = useDispatch();
  const { interestedProductsTitle, interestedProductsText } =
    window.ecom.craftConfig.interestedProducts;

  useEffect(() => {
    dispatch(resetCards());
  }, [dispatch]);
  useVeroTracking('web_checkout_complete');

  // Google Analytics purchase event. Deduplication lives inside the action
  // itself, because the same event is also dispatched by the checkout poll when
  // payment completes - so this view can dispatch unconditionally and the
  // action decides whether the booking has already been counted (SBAU-611).
  //
  // This replaces an OS-1083 guard that returned early unless
  // surchargePaymentTypeId was null. That field is surcharge bookkeeping, not
  // checkout state - braintree hosted fields set it to a real id as soon as a
  // card number is recognised - so it deduplicated only by accident, and only
  // for payment methods that happened to set it. surchargePaymentTypeId is
  // still passed on, because the analytics module treats it as an override of
  // the payment's own type.
  // Depends on `paid` as well as `reference` so that a booking which hydrates
  // (or completes payment) after the first render still dispatches — the gate
  // in the action is what decides, not this view.
  useEffect(() => {
    dispatch(
      submitToGoogleAnalytics({ payment_type_id: surchargePaymentTypeId })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, isBookingPaid(booking)]);

  const {
    veroSubscribeTitle,
    veroSubscribeText,
    veroActionUrl,
    veroBusinessUnitTag,
    veroSuccessRedirectUrl,
    veroFailureRedirectUrl,
  } = window.ecom.craftConfig.veroSubscribe;

  const [walletPasses, setWalletPasses] = useState([]);

  useEffect(() => {
    dispatch(fetchDocuments(bookingId));

    if (bookingId) {
      EcomEngineAPI.booking
        .walletPasses(bookingId)
        .then(setWalletPasses)
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="confirm-booking space-y-4">
      <div className="h2">Thank you {contactName} for your booking!</div>
      <div className="h5">Your booking reference number is {reference}</div>
      <p>
        An email with your receipt and confirmation has been sent to{' '}
        {contactEmail}.
      </p>
      <EcomComponentWrapper
        className="travel-docs"
        title="Your travel documents"
      >
        <ul className="list-unstyled text-primary space-y-2">
          {documents &&
            documents.map((doc) => (
              <li className="travel-docs__item" key={doc.id}>
                <a
                  className="travel-docs__link"
                  target="_blank"
                  title={doc.name}
                  href={generateURL(
                    `/booking/${bookingId}/documents/${doc.id}?display=true`
                  )}
                  rel="noreferrer"
                >
                  <i className="fas fa-file-pdf me-2" />
                  {doc.name}
                </a>
              </li>
            ))}
        </ul>
      </EcomComponentWrapper>

      <WalletPasses walletPasses={walletPasses} />

      <div className="d-lg-none">
        <CartSummary readOnly paid title="Order Confirmation" />
      </div>

      <RelatedProducts
        title={interestedProductsTitle}
        text={interestedProductsText}
        type="interestedProducts"
        bookingId={bookingId}
      />

      <NewsletterSubscribe
        {...{
          veroSubscribeTitle,
          veroSubscribeText,
          veroActionUrl,
          veroBusinessUnitTag,
          veroSuccessRedirectUrl,
          veroFailureRedirectUrl,
        }}
        className="newsletter-subscribe"
        userFullName={contactName}
        userEmail={contactEmail}
      />
    </div>
  );
}
